// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Site KPI evaluation over the already-loaded twin subgraph.
 *
 * Reads child telemetry entities directly rather than going through
 * `aggregateTelemetryState`, which keys merged readings by child entity *type*
 * and keeps only the first value per type. That is the right behaviour for a
 * status dot but wrong for a sum: a PDU with three `ActivePower` points would
 * contribute one.
 */

import type { Entity, Relationship, ValueEnvelope } from '../types/digitalTwin';
import { buildTelemetryChildIndex, type StatusToken } from './entityHelpers';
import { KG_KPI_DEFS, type KgKpiAggregate, type KgKpiDef } from '../config/knowledgeGraphKpis';

export interface KgKpiValue {
  def: KgKpiDef;
  /** Null when nothing contributed — rendered as an explicit gap, not a zero. */
  value: number | null;
  /** How many readings went into the value, so the number is auditable. */
  contributingCount: number;
  status: StatusToken;
  /** Populated when the metric cannot be sourced at all. */
  unavailableReason?: string;
}

const numericValue = (envelope: unknown): number | null => {
  if (!envelope || typeof envelope !== 'object' || !('value' in envelope)) return null;
  const raw = (envelope as ValueEnvelope).value;
  const num = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(num) ? num : null;
};

const aggregateValues = (values: number[], aggregate: KgKpiAggregate): number => {
  switch (aggregate) {
    case 'sum':
      return values.reduce((total, v) => total + v, 0);
    case 'avg':
      return values.reduce((total, v) => total + v, 0) / values.length;
    case 'max':
      return Math.max(...values);
    case 'min':
      return Math.min(...values);
    default:
      return values[0];
  }
};

const statusFor = (def: KgKpiDef, value: number): StatusToken => {
  const t = def.thresholds;
  if (!t) return 'ok';
  if (t.direction === 'above') {
    if (t.fault != null && value >= t.fault) return 'fault';
    if (t.warn != null && value >= t.warn) return 'warn';
    return 'ok';
  }
  if (t.fault != null && value <= t.fault) return 'fault';
  if (t.warn != null && value <= t.warn) return 'warn';
  return 'ok';
};

/**
 * Collect every reading that matches a KPI source, from the asset's own state
 * and from its child telemetry entities.
 */
const collectReadings = (
  def: KgKpiDef,
  entities: Entity[],
  entitiesById: Map<string, Entity>,
  childIndex: ReadonlyMap<string, string[]>
): number[] => {
  const readings: number[] = [];

  for (const source of def.sources) {
    const typeMatches = (type: string) =>
      source.entityTypes.length === 0 || source.entityTypes.includes(type);

    for (const entity of entities) {
      if (!typeMatches(entity.type)) continue;

      // Inline state on the asset itself.
      for (const key of source.measurementKeys) {
        const inline = numericValue(entity.state?.[key]);
        if (inline !== null) readings.push(inline);
      }

      // Every matching child telemetry entity, not just the first per type.
      for (const childId of childIndex.get(entity.id) ?? []) {
        const child = entitiesById.get(childId);
        if (!child?.state) continue;
        if (!source.measurementKeys.includes(child.type)) continue;
        for (const value of Object.values(child.state)) {
          const num = numericValue(value);
          if (num !== null) readings.push(num);
        }
      }
    }
  }

  return readings;
};

export const evaluateKpi = (
  def: KgKpiDef,
  entities: Entity[],
  entitiesById: Map<string, Entity>,
  childIndex: ReadonlyMap<string, string[]>
): KgKpiValue => {
  if (def.unavailableReason) {
    return {
      def,
      value: null,
      contributingCount: 0,
      status: 'idle',
      unavailableReason: def.unavailableReason,
    };
  }

  const readings = collectReadings(def, entities, entitiesById, childIndex);
  if (readings.length === 0) {
    return { def, value: null, contributingCount: 0, status: 'idle' };
  }

  const raw = aggregateValues(readings, def.aggregate);
  const value = raw * (def.scale ?? 1);
  return { def, value, contributingCount: readings.length, status: statusFor(def, value) };
};

export const evaluateKpis = (
  entities: Entity[],
  relationships: Relationship[],
  defs: readonly KgKpiDef[] = KG_KPI_DEFS
): KgKpiValue[] => {
  const entitiesById = new Map(entities.map(e => [e.id, e]));
  const childIndex = buildTelemetryChildIndex(relationships);
  return defs.map(def => evaluateKpi(def, entities, entitiesById, childIndex));
};

/** Format a KPI value for display, or an em dash when there is nothing to show. */
export const formatKpiValue = (kpi: KgKpiValue): string =>
  kpi.value === null ? '—' : kpi.value.toFixed(kpi.def.precision ?? 1);
